import React, { useState } from 'react';
import { 
  Code, 
  Eye, 
  FileCode,
  Layout,
  Terminal,
  Sparkles,
  Star,
  Shield,
  Zap,
  Monitor,
  Smartphone,
  Tablet,
  Search,
  Copy,
  Download,
  Save,
  Upload,
  Settings,
  Palette,
  CheckCircle,
  AlertCircle,
  Play,
  RotateCcw,
  Maximize,
  Minimize,
  Sun,
  Moon,
  Globe,
  Database,
  GitBranch,
  History,
  Layers,
  Info,
  HelpCircle,
  Cpu,
  Paintbrush,
  FileText,
  Filter,
  Grid,
  List
} from 'lucide-react';

interface Feature {
  category: string;
  icon: React.ReactNode;
  description: string;
  items: string[];
  highlight?: boolean;
  color?: string;
}

interface HTMLViewerFeaturesProps {
  theme?: 'dark' | 'light';
  className?: string;
  showHeader?: boolean;
}

const HTMLViewerFeatures: React.FC<HTMLViewerFeaturesProps> = ({
  theme = 'light',
  className = '',
  showHeader = true,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const features: Feature[] = [
    {
      category: "Advanced Code Editor",
      icon: <Code className="w-6 h-6" />,
      description: "Professional-grade HTML editing with intelligent features",
      highlight: true,
      color: "from-blue-500 to-blue-600",
      items: [
        "Real-time syntax highlighting with color-coded HTML elements",
        "Advanced HTML validation with detailed error reporting",
        "Auto-formatting and code beautification with customizable indentation",
        "Smart bracket matching and auto-completion",
        "Line numbers with click-to-navigate functionality",
        "Configurable font size (10px-24px) and tab spacing (2-8 spaces)",
        "Word wrap toggle for better code readability",
        "Show/hide invisible characters (spaces, tabs, line breaks)",
        "Code folding and collapsing for large documents",
        "Multi-cursor editing support",
        "Find and replace with regex support",
        "Undo/redo with unlimited history"
      ]
    },
    {
      category: "Live Preview System",
      icon: <Eye className="w-6 h-6" />,
      description: "Real-time preview with advanced responsive testing capabilities",
      highlight: true,
      color: "from-purple-500 to-purple-600",
      items: [
        "Instant live preview with auto-refresh on code changes",
        "Manual preview control with force refresh option",
        "Responsive design testing with device-specific viewports",
        "Desktop preview mode (full width)",
        "Tablet preview mode (768px × 1024px)",
        "Mobile preview mode (375px × 667px)",
        "Safe sandboxed iframe execution environment",
        "Split-screen view (code + preview simultaneously)",
        "Fullscreen preview mode for better testing",
        "Preview scaling and zoom controls",
        "Device rotation simulation",
        "Performance monitoring and load time analysis"
      ]
    },
    {
      category: "File Management & Export",
      icon: <FileCode className="w-6 h-6" />,
      description: "Comprehensive file handling with multiple export formats",
      color: "from-green-500 to-green-600",
      items: [
        "Import HTML files from local file system",
        "Drag and drop file upload support",
        "Export as standard HTML files (.html)",
        "Export as plain text files (.txt)",
        "Export as JSON with metadata (content, lines, characters, timestamp)",
        "Auto-save functionality with local storage backup",
        "Manual save with custom filename options",
        "Copy entire code to clipboard with one click",
        "Quick access to pre-built HTML templates",
        "Basic HTML5 template with semantic structure",
        "Responsive grid layout template",
        "Modern glassmorphism design template",
        "Template customization and modification tools"
      ]
    },
    {
      category: "User Interface & Experience",
      icon: <Layout className="w-6 h-6" />,
      description: "Modern, customizable interface designed for productivity",
      color: "from-orange-500 to-orange-600",
      items: [
        "Dark and light theme support with system preference detection",
        "Three view modes: Code-only, Preview-only, Split-view",
        "Fullscreen editing mode for distraction-free coding",
        "Responsive interface that works on all screen sizes",
        "Customizable workspace layout and panel arrangement",
        "Collapsible code editor for more preview space",
        "Professional toolbar with intuitive icon design",
        "Status bar with real-time statistics and information",
        "Keyboard shortcuts for common actions",
        "Smooth animations and transitions throughout the interface",
        "High contrast mode for accessibility",
        "Resizable panels and flexible layout system"
      ]
    },
    {
      category: "Development Tools & Validation",
      icon: <Terminal className="w-6 h-6" />,
      description: "Professional development tools for code quality assurance",
      color: "from-red-500 to-red-600",
      items: [
        "Real-time HTML validation with W3C standards compliance",
        "Detailed error reporting with line numbers and descriptions",
        "Warning system for best practices and accessibility",
        "Performance optimization suggestions and recommendations",
        "Code quality analysis with maintainability scores",
        "Browser compatibility checking and warnings",
        "Accessibility (a11y) recommendations and guidelines",
        "SEO optimization hints and meta tag validation",
        "Character, word, and line counting with live updates",
        "Code complexity analysis and readability metrics",
        "HTML5 semantic validation and structure analysis",
        "Link validation and resource checking"
      ]
    },
    {
      category: "Search & Navigation Features",
      icon: <Search className="w-6 h-6" />,
      description: "Powerful search and navigation tools for efficient coding",
      color: "from-indigo-500 to-indigo-600",
      items: [
        "Global search with case-sensitive and case-insensitive options",
        "Regular expression (regex) search support",
        "Find and replace functionality with preview",
        "Replace all occurrences with confirmation",
        "Jump to specific line numbers",
        "Navigate between search results with keyboard shortcuts",
        "Highlight all search matches in the editor",
        "Search history for frequently used terms",
        "Quick symbol navigation and tag jumping",
        "Bookmark system for important code sections",
        "Minimap for quick code overview and navigation",
        "Scroll-to-search result with smooth animations"
      ]
    },
    {
      category: "Advanced Productivity Features",
      icon: <Sparkles className="w-6 h-6" />,
      description: "Professional features that enhance development workflow",
      highlight: true,
      color: "from-pink-500 to-pink-600",
      items: [
        "Multi-device responsive testing with real-time switching",
        "Version history and change tracking",
        "Code completion with HTML5 element suggestions",
        "Smart indentation and auto-formatting on paste",
        "Live collaboration ready with conflict resolution",
        "Plugin architecture support for extensibility",
        "Custom theme creation and color scheme editor",
        "Workspace presets for different project types",
        "Code snippet library with custom snippets",
        "Macro recording and playback for repetitive tasks",
        "Integration with external tools and services",
        "Advanced diff view for comparing code versions"
      ]
    },
    {
      category: "Performance & Optimization",
      icon: <Zap className="w-6 h-6" />,
      description: "Optimized for speed and efficiency in all operations",
      color: "from-yellow-500 to-yellow-600",
      items: [
        "Lightning-fast syntax highlighting engine",
        "Debounced auto-save to prevent performance issues",
        "Efficient memory management for large files",
        "Optimized rendering for smooth scrolling",
        "Lazy loading for better startup performance",
        "Code minification suggestions for production",
        "Image optimization recommendations",
        "CSS and JavaScript compression hints",
        "Performance budgets and size warnings",
        "Real-time performance monitoring",
        "Browser resource usage optimization",
        "Efficient DOM manipulation and updates"
      ]
    }
  ];

  const totalFeatures = features.reduce((total, feature) => total + feature.items.length, 0);

  return (
    <div className={`${className} ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} min-h-screen`}>
      {showHeader && (
        <div className={`relative overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500"></div>
            <div 
              className="absolute inset-0" 
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            ></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 py-16">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-2xl">
                    <Code className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                    <Star className="w-3 h-3 text-yellow-800" />
                  </div>
                </div>
              </div>
              
              <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Advanced HTML Viewer
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
                A comprehensive development environment featuring <span className="font-semibold text-blue-600">{totalFeatures}+ powerful features</span> across 
                <span className="font-semibold text-purple-600"> {features.length} categories</span> for professional HTML development
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className={`px-6 py-3 rounded-full ${theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'} font-semibold`}>
                  🎨 {features.length} Feature Categories
                </div>
                <div className={`px-6 py-3 rounded-full ${theme === 'dark' ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700'} font-semibold`}>
                  ⚡ {totalFeatures} Total Features
                </div>
                <div className={`px-6 py-3 rounded-full ${theme === 'dark' ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'} font-semibold`}>
                  🚀 Production Ready
                </div>
              </div>
            </div>

            <div className="flex justify-center mb-8">
              <div className={`flex items-center space-x-1 p-1 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    viewMode === 'grid'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  <span>Grid View</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    viewMode === 'list'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span>List View</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-12">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 hover:scale-105 ${
                  selectedCategory === index
                    ? 'ring-4 ring-blue-500 shadow-2xl shadow-blue-500/25'
                    : 'hover:shadow-2xl'
                } ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}
                onClick={() => setSelectedCategory(selectedCategory === index ? null : index)}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
                
                {feature.highlight && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                
                <div className="relative p-6 h-full flex flex-col">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${feature.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-1 group-hover:text-blue-600 transition-colors">
                        {feature.category}
                      </h3>
                      <div className={`text-xs font-medium px-2 py-1 rounded-full w-fit ${
                        theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {feature.items.length} features
                      </div>
                    </div>
                  </div>
                  
                  <p className={`text-sm leading-relaxed mb-4 flex-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {feature.description}
                  </p>
                  
                  <div className="mt-auto">
                    <div className={`w-full h-1 rounded-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className={`h-full rounded-full bg-gradient-to-r ${feature.color} transition-all duration-300 ${
                          selectedCategory === index ? 'w-full' : 'w-0 group-hover:w-1/2'
                        }`}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-2xl transition-all duration-300 ${
                  selectedCategory === index
                    ? 'ring-2 ring-blue-500 shadow-xl'
                    : 'hover:shadow-lg'
                } ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => setSelectedCategory(selectedCategory === index ? null : index)}
                >
                  <div className="flex items-center space-x-6">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${feature.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                      {feature.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="font-bold text-xl group-hover:text-blue-600 transition-colors">
                          {feature.category}
                        </h3>
                        {feature.highlight && (
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="text-xs font-medium px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                              Premium
                            </span>
                          </div>
                        )}
                        <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                          theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {feature.items.length} features
                        </div>
                      </div>
                      <p className={`text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {feature.description}
                      </p>
                    </div>
                    
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      selectedCategory === index
                        ? 'bg-blue-500 text-white'
                        : theme === 'dark'
                          ? 'bg-gray-700 text-gray-400'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Info className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedCategory !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-4xl w-full max-h-[80vh] overflow-y-auto rounded-2xl ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            } shadow-2xl`}>
              <div className="sticky top-0 z-10 p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-purple-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-xl bg-white/20 text-white">
                      {features[selectedCategory].icon}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        {features[selectedCategory].category}
                      </h2>
                      <p className="text-blue-100">
                        {features[selectedCategory].description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {features[selectedCategory].items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className={`flex items-start space-x-3 p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] ${
                        theme === 'dark'
                          ? 'border-gray-700 bg-gray-900/50 hover:bg-gray-700/50'
                          : 'border-gray-200 bg-gray-50 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                      </div>
                      <span className={`text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HTMLViewerFeatures;